
import { Link, useNavigate } from 'react-router-dom';
import React from 'react';

interface ResultsCTAProps {
  status: 'eligible' | 'conditional' | 'not-eligible';
}

/**
 * ResultsCTA component displays a call‑to‑action based on the eligibility status.
 * It scrolls to the contact section after navigating to the home page.
 */
export function ResultsCTA({ status }: ResultsCTAProps) {
  const navigate = useNavigate();

  /**
   * Navigates to the home page and then scrolls smoothly to the #contact element.
   * Includes basic error handling to avoid runtime crashes if the element is missing.
   */
  const handleScrollToContact = () => {
    try {
      // Use react‑router's navigation instead of a non‑existent global function.
      navigate('/', { replace: true });

      // Wait for the navigation to settle before attempting to scroll.
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          // Optional: you could log a warning for developers.
          console.warn('Element with id "contact" not found.');
        }
      }, 500);
    } catch (err) {
      // Gracefully handle unexpected errors (e.g., navigate not available).
      console.error('Failed to navigate and scroll to contact:', err);
    }
  };

  return (
    <section className="py-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-gradient-to-br from-[#505A5A] to-[#3D4444] rounded-2xl p-8 lg:p-12 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-6">
            <i
              className={`text-4xl text-white ${
                status === 'eligible'
                  ? 'ri-rocket-2-line'
                  : status === 'conditional'
                  ? 'ri-compass-3-line'
                  : 'ri-customer-service-2-line'
              }`}
            ></i>
          </div>

          {status === 'eligible' && (
            <>
              <h2 className="font-['Fraunces'] text-3xl font-bold text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-white/80 leading-relaxed mb-8 max-w-lg mx-auto">
                Your property looks like a strong candidate. Let us handle the compliance paperwork,
                licensing, and listing setup so you can start earning.
              </p>
            </>
          )}

          {status === 'conditional' && (
            <>
              <h2 className="font-['Fraunces'] text-3xl font-bold text-white mb-4">
                Let Us Help You Get Compliant
              </h2>
              <p className="text-white/80 leading-relaxed mb-8 max-w-lg mx-auto">
                A few things need to be resolved before you can legally host. We specialize in navigating
                these exact issues — book a free call and we’ll map out your path.
              </p>
            </>
          )}

          {status === 'not-eligible' && (
            <>
              <h2 className="font-['Fraunces'] text-3xl font-bold text-white mb-4">
                Don’t Give Up — There May Be Options
              </h2>
              <p className="text-white/80 leading-relaxed mb-8 max-w-lg mx-auto">
                Even if short-term hosting isn’t possible right now, medium‑term furnished rentals (30+
                days) are often allowed. Let’s explore your alternatives together.
              </p>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleScrollToContact}
              className="bg-[#882629] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#6E1F22] transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              Book a Free Consultation
            </button>
            <Link
              to="/"
              className="border-2 border-white/30 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </Link>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#505A5A]/40 leading-relaxed max-w-xl mx-auto">
            This eligibility assessment is for informational purposes only and does not constitute
            legal advice. Regulations change frequently. Always verify current rules with your local
            municipality and consult a qualified professional before making hosting decisions.
          </p>
        </div>
      </div>
    </section>
  );
}
